import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';

import { Button, Dialog, TextField } from '@zendesk/forethought-elements';
import {
  useCreateAbTestConfigMutation,
  useRenameAbTestConfigMutation,
} from 'src/services/agent-ab-test/agentAbTestApi';
import {
  SolveABTestConfig,
  SolveABTestConfigSummary,
  VARIANT_DEFAULT_NAMES,
} from 'src/services/agent-ab-test/types';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';
import { SolveWidgetProduct } from 'src/types/types';
import { AutoflowVersionHistorySummary } from 'src/types/workflowBuilderAPITypes';
import { Routes } from 'src/utils/enums';

interface CreateOrRenameAbTestDialogProps {
  abTestConfig?: SolveABTestConfigSummary | SolveABTestConfig;
  intentId?: string;
  onClose: () => void;
  open: boolean;
  product?: SolveWidgetProduct;
  versionData?: AutoflowVersionHistorySummary;
}

interface FormData {
  description: string;
  name: string;
}

const defaultFormData: FormData = {
  description: '',
  name: '',
};

const CreateOrRenameAbTestDialog = ({
  abTestConfig,
  intentId,
  onClose,
  open,
  product,
  versionData,
}: CreateOrRenameAbTestDialogProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('agent-ab-test');
  const [createAbTestConfig, { isLoading: isCreating }] =
    useCreateAbTestConfigMutation();
  const [renameAbTestConfig, { isLoading: isRenaming }] =
    useRenameAbTestConfigMutation();

  const { control, formState, handleSubmit, reset } = useForm<FormData>({
    defaultValues: abTestConfig
      ? { description: abTestConfig.description || '', name: abTestConfig.name }
      : defaultFormData,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      title={
        abTestConfig
          ? t('createOrRenameDialog.titleRename')
          : t('createOrRenameDialog.titleCreate')
      }
    >
      <Box
        component='form'
        noValidate
        onSubmit={handleSubmit(async data => {
          try {
            const { ab_test_config_id } = abTestConfig
              ? await renameAbTestConfig({
                  abTestConfigId: abTestConfig.ab_test_config_id,
                  body: {
                    description: data.description,
                    name: data.name,
                  },
                }).unwrap()
              : await createAbTestConfig({
                  description: data.description,
                  name: data.name,
                  product: product || 'workflow_builder',
                  timezone:
                    Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
                  ...(intentId ? { intent_workflow_id: intentId } : {}),
                  ...(versionData
                    ? {
                        variants: [
                          {
                            intent_workflow_version: versionData.version,
                            is_control: true,
                            name: VARIANT_DEFAULT_NAMES[0],
                            traffic_percentage: 100,
                            version_type: versionData.version_type,
                          },
                        ],
                      }
                    : {}),
                }).unwrap();

            handleClose();

            if (abTestConfig) {
              dispatch(
                setGlobalToastOptions({
                  autoHideDuration: 5000,
                  title: t('toast.renamed', { name: data.name }),
                  variant: 'main',
                }),
              );
            } else {
              navigate(`${Routes.AGENT_AB_TESTS}/${ab_test_config_id}`);
            }
          } catch {
            dispatch(
              setGlobalToastOptions({
                autoHideDuration: 5000,
                title: abTestConfig
                  ? t('toast.renameFailed', { name: data.name })
                  : t('toast.createFailed', { name: data.name }),
                variant: 'danger',
              }),
            );
          }
        })}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          height: '568px',
          width: '628px',
        }}
      >
        <Controller
          control={control}
          name='name'
          render={({ field, fieldState }) => {
            // eslint-disable-next-line unused-imports/no-unused-vars -- omit ref to avoid warning in browser console
            const { ref, ...rest } = field;
            return (
              <TextField
                {...rest}
                autoComplete='off'
                error={fieldState.error?.message}
                label={t('createOrRenameDialog.nameLabel')}
                placeholder={t('createOrRenameDialog.namePlaceholder')}
              />
            );
          }}
          rules={{
            required: t('createOrRenameDialog.nameRequired'),
          }}
        />
        <Controller
          control={control}
          name='description'
          render={({ field, fieldState }) => {
            // eslint-disable-next-line unused-imports/no-unused-vars -- omit ref to avoid warning in browser console
            const { ref, ...rest } = field;
            return (
              <TextField
                {...rest}
                error={fieldState.error?.message}
                label={t('createOrRenameDialog.descriptionLabel')}
                multiline
                placeholder={t('createOrRenameDialog.descriptionPlaceholder')}
                rows={4}
              />
            );
          }}
          rules={{
            required: t('createOrRenameDialog.descriptionRequired'),
          }}
        />
        <Box
          sx={{
            alignItems: 'end',
            display: 'flex',
            gap: '8px',
            height: '100%',
            justifyContent: 'flex-end',
          }}
        >
          <Button onClick={handleClose} variant='ghost'>
            {t('createOrRenameDialog.cancelButton')}
          </Button>
          <Button
            disabled={!formState.isValid}
            fullWidth
            isLoading={isCreating || isRenaming}
            type='submit'
          >
            {abTestConfig
              ? t('createOrRenameDialog.submitRename')
              : t('createOrRenameDialog.submitCreate')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CreateOrRenameAbTestDialog;
